import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { 
  Users, MessageCircle, Send, ChevronRight, Instagram, Globe, 
  Plus, ArrowLeft, User, Sparkles, X, Edit2, Check, Bell, MapPin, Heart
} from 'lucide-react';

const Community = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [questions, setQuestions] = useState([]);
  const [members, setMembers] = useState([]);
  const [myProfile, setMyProfile] = useState(null);
  const [myQuestions, setMyQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [showIntroPrompt, setShowIntroPrompt] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ title: '', content: '' });
  const [newAnswer, setNewAnswer] = useState('');
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    activity: '',
    description: '',
    instagram: '',
    website: ''
  });
  const [newRepliesCount, setNewRepliesCount] = useState(0);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [questionsRes, membersRes, profileRes, myQuestionsRes] = await Promise.all([
        axios.get(`${API}/community/questions`, { headers }),
        axios.get(`${API}/community/members`, { headers }),
        axios.get(`${API}/community/members/me`, { headers }),
        axios.get(`${API}/community/questions/my`, { headers }).catch(() => ({ data: { questions: [] } }))
      ]);
      
      setQuestions(questionsRes.data.questions);
      setMembers(membersRes.data.members);
      setMyProfile(profileRes.data);
      setMyQuestions(myQuestionsRes.data.questions || []);
      
      setProfileForm({
        activity: profileRes.data.activity || '',
        description: profileRes.data.description || '',
        instagram: profileRes.data.instagram || '',
        website: profileRes.data.website || ''
      });

      // Check if profile is incomplete
      const isProfileIncomplete = !profileRes.data.activity && !profileRes.data.description;
      if (isProfileIncomplete) {
        setShowProfilePrompt(true);
      }

      // Check for new replies on my questions
      const totalReplies = (myQuestionsRes.data.questions || []).reduce((sum, q) => sum + (q.answer_count || 0), 0);
      const lastSeenReplies = parseInt(localStorage.getItem('lastSeenReplies') || '0');
      if (totalReplies > lastSeenReplies) {
        setNewRepliesCount(totalReplies - lastSeenReplies);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostQuestion = async () => {
    if (!newQuestion.title.trim() || !newQuestion.content.trim()) {
      toast.error('Titre et contenu requis');
      return;
    }
    
    try {
      await axios.post(`${API}/community/questions`, newQuestion, { headers });
      toast.success('Question publiée !');
      setNewQuestion({ title: '', content: '' });
      setShowNewQuestion(false);
      setShowIntroPrompt(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handlePostAnswer = async () => {
    if (!newAnswer.trim() || !selectedQuestion) return;
    
    try {
      await axios.post(
        `${API}/community/questions/${selectedQuestion.question_id}/answers`,
        { content: newAnswer },
        { headers }
      );
      toast.success('Réponse ajoutée !');
      setNewAnswer('');
      const res = await axios.get(`${API}/community/questions/${selectedQuestion.question_id}`, { headers });
      setSelectedQuestion(res.data);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await axios.put(`${API}/community/members/me`, profileForm, { headers });
      toast.success('Profil mis à jour !');
      setEditingProfile(false);
      setShowProfilePrompt(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleIntroduction = () => {
    setNewQuestion({
      title: 'Présentation : Qui suis-je ?',
      content: `Bonjour à toutes ! 👋

Je m'appelle ${user?.name || '[ton prénom]'}.

Ce que je construis :
[Décris ton projet, ton business, ou ta vision]

D'où je viens :
[Ta ville ou ton pays]

Ce que je cherche ici :
[Ce que tu espères trouver dans cette communauté]

Au plaisir d'échanger avec vous !`
    });
    setShowNewQuestion(true);
    setShowIntroPrompt(false);
  };

  const dismissRepliesNotification = () => {
    const totalReplies = myQuestions.reduce((sum, q) => sum + (q.answer_count || 0), 0);
    localStorage.setItem('lastSeenReplies', totalReplies.toString());
    setNewRepliesCount(0);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const isProfileComplete = myProfile?.activity || myProfile?.description;

  return (
    <DashboardLayout title={t('community.title')}>
      <div className="max-w-5xl mx-auto">
        
        {/* Profile Completion Prompt */}
        <AnimatePresence>
          {showProfilePrompt && !loading && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <div className="relative overflow-hidden border border-[#D4AF37]/20 bg-gradient-to-r from-[#D4AF37]/5 via-transparent to-[#D4AF37]/5">
                <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
                <div className="p-8 text-center">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center">
                      <User className="text-[#D4AF37]" size={28} />
                    </div>
                    
                    <p className="text-[#A1A1AA] text-sm mb-2 font-light">Avant d'explorer la communauté…</p>
                    
                    <h2 className="font-serif text-2xl text-white mb-4">
                      Prends un instant pour te présenter.
                    </h2>
                    
                    <p className="text-[#71717A] text-sm max-w-md mx-auto mb-8 leading-relaxed">
                      C'est comme ça que les autres peuvent te voir, te comprendre… et se connecter à toi.
                    </p>
                    
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        onClick={() => { setActiveTab('profile'); setEditingProfile(true); setShowProfilePrompt(false); }}
                        className="btn-gold px-6 py-3 flex items-center gap-2"
                        data-testid="complete-profile-btn"
                      >
                        Compléter mon profil
                        <ChevronRight size={16} />
                      </Button>
                      <button
                        onClick={() => setShowProfilePrompt(false)}
                        className="text-[#52525B] text-sm hover:text-[#71717A] transition-colors"
                      >
                        Plus tard
                      </button>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* New Replies Notification */}
        <AnimatePresence>
          {newRepliesCount > 0 && !showProfilePrompt && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6"
            >
              <div 
                className="flex items-center justify-between p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5 cursor-pointer hover:bg-[#D4AF37]/10 transition-colors"
                onClick={() => {
                  dismissRepliesNotification();
                  // Find a question with replies and show it
                  const questionWithReplies = myQuestions.find(q => q.answer_count > 0);
                  if (questionWithReplies) {
                    setSelectedQuestion(questionWithReplies);
                    setActiveTab('feed');
                  }
                }}
                data-testid="replies-notification"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Bell className="text-[#D4AF37]" size={18} />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">
                      Quelqu'un t'a répondu dans la communauté
                    </p>
                    <p className="text-[#71717A] text-xs">
                      {newRepliesCount} nouvelle{newRepliesCount > 1 ? 's' : ''} réponse{newRepliesCount > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <ChevronRight className="text-[#D4AF37]" size={18} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        {!showProfilePrompt && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
              <Sparkles className="text-[#D4AF37]" size={16} />
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            </div>
            <h1 className="font-serif text-3xl text-white mb-3">Clierxter Global Women Community</h1>
            <p className="text-[#71717A] text-sm max-w-lg mx-auto">
              Un espace d'échange et de soutien entre femmes entrepreneures
            </p>
          </motion.div>
        )}

        {/* Tabs */}
        {!showProfilePrompt && (
          <div className="flex justify-center gap-2 mb-10">
            {[
              { id: 'feed', label: t('community.tabs.questions'), icon: MessageCircle },
              { id: 'members', label: t('community.tabs.members'), icon: Users },
              { id: 'profile', label: t('community.tabs.myProfile'), icon: User }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSelectedQuestion(null); }}
                className={`flex items-center gap-2 px-6 py-3 text-sm transition-all ${
                  activeTab === tab.id 
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' 
                    : 'text-[#71717A] hover:text-white border border-transparent'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          {/* Feed Tab */}
          {activeTab === 'feed' && !selectedQuestion && !showProfilePrompt && (
            <motion.div
              key="feed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Introduction Prompt - Show if user hasn't posted yet */}
              {!showNewQuestion && questions.filter(q => q.user_id === user?.user_id).length === 0 && !showIntroPrompt && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-8"
                >
                  <div className="card-luxury p-8 text-center border-[#D4AF37]/10">
                    <Heart className="text-[#D4AF37]/60 mx-auto mb-4" size={32} />
                    <h3 className="font-serif text-xl text-white mb-3">
                      J'aimerais que tu fasses une première chose simple.
                    </h3>
                    <p className="text-[#A1A1AA] text-sm mb-2">Va dans la communauté.</p>
                    <p className="text-white font-medium mb-6">Et présente-toi.</p>
                    
                    <div className="text-[#71717A] text-sm mb-6 space-y-1">
                      <p>Dis-nous :</p>
                      <p>– qui tu es</p>
                      <p>– ce que tu construis</p>
                      <p>– et d'où tu viens (ton pays ou ta ville)</p>
                    </div>
                    
                    <p className="text-[#52525B] text-xs mb-6 italic">
                      Pas besoin que ce soit parfait. Juste vrai.
                    </p>
                    <p className="text-[#D4AF37] text-sm mb-8">
                      C'est comme ça que les connexions commencent.
                    </p>
                    
                    <Button
                      onClick={handleIntroduction}
                      className="btn-gold px-8 py-3"
                      data-testid="intro-prompt-btn"
                    >
                      Me présenter
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* New Question Button */}
              {!showNewQuestion && questions.filter(q => q.user_id === user?.user_id).length > 0 && (
                <motion.button
                  onClick={() => setShowNewQuestion(true)}
                  className="w-full card-luxury p-6 mb-8 flex items-center justify-center gap-3 text-[#71717A] hover:text-[#D4AF37] hover:border-[#D4AF37]/30 transition-all group"
                  data-testid="new-question-btn"
                >
                  <Plus size={20} className="group-hover:rotate-90 transition-transform" />
                  <span>Poser une question à la communauté</span>
                </motion.button>
              )}
              
              {/* New Question Form */}
              {showNewQuestion && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="card-luxury p-6 mb-8"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-serif text-lg text-white">
                      {newQuestion.title.includes('Présentation') ? 'Présente-toi à la communauté' : 'Nouvelle question'}
                    </h3>
                    <button onClick={() => { setShowNewQuestion(false); setNewQuestion({ title: '', content: '' }); }} className="text-[#52525B] hover:text-white">
                      <X size={18} />
                    </button>
                  </div>
                  
                  {newQuestion.title.includes('Présentation') && (
                    <p className="text-[#71717A] text-sm mb-4">
                      Présente-toi… qui tu es, ce que tu construis, et d'où tu viens.
                    </p>
                  )}
                  
                  <Input
                    value={newQuestion.title}
                    onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
                    placeholder="Titre de ta question"
                    className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none mb-4 text-white placeholder:text-[#52525B]"
                    data-testid="question-title-input"
                  />
                  <Textarea
                    value={newQuestion.content}
                    onChange={(e) => setNewQuestion({ ...newQuestion, content: e.target.value })}
                    placeholder="Détaille ta question..."
                    className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none min-h-[150px] mb-4 text-white placeholder:text-[#52525B] resize-none"
                    data-testid="question-content-input"
                  />
                  <div className="flex justify-end">
                    <Button onClick={handlePostQuestion} className="btn-gold px-6" data-testid="submit-question-btn">
                      {t('community.questions.post')}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Questions List */}
              <div className="space-y-4">
                {questions.length > 0 ? (
                  questions.map((q, index) => (
                    <motion.div
                      key={q.question_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedQuestion(q)}
                      className="card-luxury p-6 cursor-pointer hover:border-[#D4AF37]/20 transition-all group"
                      data-testid={`question-${q.question_id}`}
                    >
                      <div className="flex items-start gap-4">
                        {q.user_picture ? (
                          <img src={q.user_picture} alt={q.user_name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-[#1C1C1C] rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[#D4AF37] font-serif">{q.user_name?.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium mb-1 group-hover:text-[#D4AF37] transition-colors">
                            {q.title}
                          </h3>
                          <p className="text-[#71717A] text-sm line-clamp-2 mb-3">{q.content}</p>
                          <div className="flex items-center gap-4 text-xs text-[#52525B]">
                            <span>{q.user_name}</span>
                            {q.user_activity && <span className="text-[#D4AF37]/60">• {q.user_activity}</span>}
                            <span>• {formatDate(q.created_at)}</span>
                            <span className="flex items-center gap-1 text-[#71717A]">
                              <MessageCircle size={12} />
                              {q.answer_count || 0} réponse{(q.answer_count || 0) > 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="text-[#3F3F46] group-hover:text-[#D4AF37] transition-colors flex-shrink-0" size={20} />
                      </div>
                    </motion.div>
                  ))
                ) : !showNewQuestion && questions.filter(q => q.user_id === user?.user_id).length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card-luxury p-12 text-center">
                    <MessageCircle className="text-[#27272A] mx-auto mb-4" size={48} />
                    <h3 className="font-serif text-xl text-white mb-2">Aucune question pour l'instant</h3>
                    <p className="text-[#71717A] text-sm">Sois la première à poser une question !</p>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {/* Selected Question Detail */}
          {activeTab === 'feed' && selectedQuestion && !showProfilePrompt && (
            <motion.div
              key="question-detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <button
                onClick={() => setSelectedQuestion(null)}
                className="flex items-center gap-2 text-[#71717A] hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft size={16} />
                Retour aux questions
              </button>

              {/* Question */}
              <div className="card-luxury p-8 mb-6">
                <div className="flex items-start gap-4 mb-6">
                  {selectedQuestion.user_picture ? (
                    <img src={selectedQuestion.user_picture} alt={selectedQuestion.user_name} className="w-12 h-12 rounded-full" />
                  ) : (
                    <div className="w-12 h-12 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                      <span className="text-[#D4AF37] font-serif text-lg">{selectedQuestion.user_name?.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <h2 className="font-serif text-xl text-white mb-1">{selectedQuestion.title}</h2>
                    <div className="flex items-center gap-2 text-sm text-[#71717A]">
                      <span>{selectedQuestion.user_name}</span>
                      {selectedQuestion.user_activity && (
                        <span className="text-[#D4AF37]/60">• {selectedQuestion.user_activity}</span>
                      )}
                      <span>• {formatDate(selectedQuestion.created_at)}</span>
                    </div>
                  </div>
                </div>
                <p className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap">{selectedQuestion.content}</p>
              </div>

              {/* Answers */}
              <div className="mb-6">
                <h3 className="text-white text-sm font-medium mb-4">
                  {selectedQuestion.answers?.length || 0} réponse{(selectedQuestion.answers?.length || 0) > 1 ? 's' : ''}
                </h3>
                
                {selectedQuestion.answers?.length > 0 ? (
                  <div className="space-y-4">
                    {selectedQuestion.answers.map((answer, index) => (
                      <motion.div
                        key={answer.answer_id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="card-luxury p-6"
                      >
                        <div className="flex items-start gap-3">
                          {answer.user_picture ? (
                            <img src={answer.user_picture} alt={answer.user_name} className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 bg-[#1C1C1C] rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-[#D4AF37] font-serif text-sm">{answer.user_name?.charAt(0)}</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2 text-sm">
                              <span className="text-white">{answer.user_name}</span>
                              {answer.user_activity && (
                                <span className="text-[#D4AF37]/60">• {answer.user_activity}</span>
                              )}
                              <span className="text-[#52525B]">• {formatDate(answer.created_at)}</span>
                            </div>
                            <p className="text-[#A1A1AA] text-sm leading-relaxed whitespace-pre-wrap">{answer.content}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#52525B] text-sm">Aucune réponse pour l'instant. Sois la première à aider !</p>
                )}
              </div>

              {/* Add Answer */}
              <div className="card-luxury p-6">
                <h4 className="text-white text-sm font-medium mb-4">Ta réponse</h4>
                <Textarea
                  value={newAnswer}
                  onChange={(e) => setNewAnswer(e.target.value)}
                  placeholder="Partage ton expérience ou tes conseils..."
                  className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none min-h-[100px] mb-4 text-white placeholder:text-[#52525B] resize-none"
                  data-testid="answer-input"
                />
                <div className="flex justify-end">
                  <Button 
                    onClick={handlePostAnswer} 
                    disabled={!newAnswer.trim()}
                    className="btn-gold px-6 flex items-center gap-2"
                    data-testid="submit-answer-btn"
                  >
                    <Send size={14} />
                    {t('community.questions.reply')}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && !showProfilePrompt && (
            <motion.div
              key="members"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {members.length > 0 ? (
                  members.map((member, index) => (
                    <motion.div
                      key={member.user_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="card-luxury p-6"
                      data-testid={`member-${member.user_id}`}
                    >
                      <div className="flex items-start gap-4">
                        {member.picture ? (
                          <img src={member.picture} alt={member.name} className="w-14 h-14 rounded-full" />
                        ) : (
                          <div className="w-14 h-14 bg-[#1C1C1C] rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-[#D4AF37] font-serif text-xl">{member.name?.charAt(0)}</span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium truncate">{member.name}</h3>
                          {member.activity && (
                            <p className="text-[#D4AF37] text-sm truncate">{member.activity}</p>
                          )}
                        </div>
                      </div>
                      
                      {member.description && (
                        <p className="text-[#71717A] text-sm mt-4 line-clamp-2">{member.description}</p>
                      )}
                      
                      {(member.instagram || member.website) && (
                        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#1C1C1C]">
                          {member.instagram && (
                            <a
                              href={`https://instagram.com/${member.instagram.replace('@', '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#52525B] hover:text-[#D4AF37] transition-colors"
                            >
                              <Instagram size={16} />
                            </a>
                          )}
                          {member.website && (
                            <a
                              href={member.website.startsWith('http') ? member.website : `https://${member.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#52525B] hover:text-[#D4AF37] transition-colors"
                            >
                              <Globe size={16} />
                            </a>
                          )}
                        </div>
                      )}
                    </motion.div>
                  ))
                ) : (
                  <div className="col-span-full card-luxury p-12 text-center">
                    <Users className="text-[#27272A] mx-auto mb-4" size={48} />
                    <h3 className="font-serif text-xl text-white mb-2">Aucun membre pour l'instant</h3>
                    <p className="text-[#71717A] text-sm">La communauté se construit...</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && !showProfilePrompt && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-xl mx-auto"
            >
              <div className="card-luxury p-8">
                {/* Profile Header */}
                <div className="flex items-start gap-4 mb-8">
                  {myProfile?.picture ? (
                    <img src={myProfile.picture} alt={myProfile.name} className="w-20 h-20 rounded-full" />
                  ) : (
                    <div className="w-20 h-20 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                      <span className="text-[#D4AF37] font-serif text-3xl">{myProfile?.name?.charAt(0)}</span>
                    </div>
                  )}
                  <div className="flex-1">
                    <h2 className="font-serif text-2xl text-white">{myProfile?.name || 'Mon Profil'}</h2>
                    <p className="text-[#71717A] text-sm mt-1">
                      Ton profil est visible par les autres membres de la communauté
                    </p>
                  </div>
                  {!editingProfile && (
                    <button
                      onClick={() => setEditingProfile(true)}
                      className="text-[#52525B] hover:text-[#D4AF37] transition-colors"
                    >
                      <Edit2 size={18} />
                    </button>
                  )}
                </div>

                {/* Profile Form */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-[#71717A] text-sm mb-2">Activité / Business</label>
                    {editingProfile ? (
                      <Input
                        value={profileForm.activity}
                        onChange={(e) => setProfileForm({ ...profileForm, activity: e.target.value })}
                        placeholder="Ex: Coach en image de marque"
                        className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none text-white placeholder:text-[#52525B]"
                        data-testid="profile-activity"
                      />
                    ) : (
                      <p className="text-white">{profileForm.activity || <span className="text-[#52525B]">Non renseigné</span>}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-[#71717A] text-sm mb-2">Description courte</label>
                    {editingProfile ? (
                      <Textarea
                        value={profileForm.description}
                        onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                        placeholder="Quelques mots sur toi et ton parcours..."
                        className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none min-h-[80px] text-white placeholder:text-[#52525B] resize-none"
                        data-testid="profile-description"
                      />
                    ) : (
                      <p className="text-white whitespace-pre-wrap">{profileForm.description || <span className="text-[#52525B]">Non renseigné</span>}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#71717A] text-sm mb-2">Instagram</label>
                      {editingProfile ? (
                        <Input
                          value={profileForm.instagram}
                          onChange={(e) => setProfileForm({ ...profileForm, instagram: e.target.value })}
                          placeholder="@toncompte"
                          className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none text-white placeholder:text-[#52525B]"
                          data-testid="profile-instagram"
                        />
                      ) : (
                        <p className="text-white">{profileForm.instagram || <span className="text-[#52525B]">Non renseigné</span>}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-[#71717A] text-sm mb-2">Site web</label>
                      {editingProfile ? (
                        <Input
                          value={profileForm.website}
                          onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                          placeholder="www.tonsite.com"
                          className="bg-transparent border-[#27272A] focus:border-[#D4AF37] rounded-none text-white placeholder:text-[#52525B]"
                          data-testid="profile-website"
                        />
                      ) : (
                        <p className="text-white">{profileForm.website || <span className="text-[#52525B]">Non renseigné</span>}</p>
                      )}
                    </div>
                  </div>

                  {editingProfile && (
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        onClick={() => {
                          setEditingProfile(false);
                          setProfileForm({
                            activity: myProfile?.activity || '',
                            description: myProfile?.description || '',
                            instagram: myProfile?.instagram || '',
                            website: myProfile?.website || ''
                          });
                        }}
                        className="btn-outline-gold px-6"
                      >
                        Annuler
                      </Button>
                      <Button onClick={handleUpdateProfile} className="btn-gold px-6 flex items-center gap-2" data-testid="save-profile-btn">
                        <Check size={14} />
                        Enregistrer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default Community;
